import { useEffect, useRef, useState } from 'react';
import Picker from '@emoji-mart/react'
import emoji_data from '@emoji-mart/data';
import * as linkify from "linkifyjs";
import { MdOutlineMoreVert } from "react-icons/md";
import { useLocation, useParams } from 'react-router-dom';
import SpeechRecognition, { useSpeechRecognition } from 'react-speech-recognition';
import toast from 'react-hot-toast'
import { useDispatch, useSelector } from 'react-redux';
import { useMutation } from '@apollo/client';
import { sendMsgMutation } from '../../../apollo/Mutations';
import { aesEncrypt, modifyMyMsg, scrollToBottom } from '../../../Utils/Common';
import { set_messages, setPopup } from '../../../redux/messageSlice';
import VoiceMessageCon from './VoiceMessageCon';
import { useClickAway } from '@uidotdev/usehooks';
import MentionPopup from '../../CustomEditor/MentionPopup';
import CustomEditorSkeleton from '../../Skeleton/CustomEditorSkeleton';

var placeCaretAtEnd = (el) => {
    el.focus();

    if (typeof window.getSelection != "undefined" &&
        typeof document.createRange != "undefined") {
        var range = document.createRange();
        range.selectNodeContents(el);
        range.collapse(false);
        var sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
    } else if (typeof document.body.createTextRange != "undefined") {
        var textRange = document.body.createTextRange();
        textRange.moveToElementText(el);
        textRange.collapse(false);
        textRange.select();
    }
};

const CustomEditor = ({ type, position = 'relative', ...props }) => {
    const dispatch = useDispatch()
    const params = useParams()
    const location = useLocation()
    const { user, allUsers, chat_loader, msgs, reply_msgs, active_conversation, upload_popup, reply_main_msg } = useSelector(state => state.message)
    const messages = props.is_reply_msg === 'no' ? msgs : reply_msgs
    const [moreOptions, setMoreOptions] = useState(false);
    const [emojiView, setEmojiView] = useState(false);
    const [voice, setVoice] = useState(false);
    const [voice_record, setVoice_record] = useState(false);
    const editorRef = useRef(null);
    const savedRangeRef = useRef(null);
    const [mentionUsers, setMentionUsers] = useState([]);
    const [showPopup, setShowPopup] = useState(false);
    const [searchText, setSearchText] = useState('');
    // const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [editorHtml, setEditorHtml] = useState('');

    const ref = useClickAway(() => {
        setMoreOptions(false);
    });
    const emojiRef = useClickAway(() => {
        setEmojiView(false);
    });

    const MentionUsers = () => {
        if (!active_conversation?.group || active_conversation?.group !== 'yes') return [];

        // Get participant details
        const participantDetails = allUsers.filter(user => active_conversation?.participants.includes(user?.id));

        // Build mention list
        const mentions = participantDetails
            .filter(u => u?.id !== user?.id) // Exclude the current user
            .map(user => ({
                text: `${user.firstname} ${user.lastname}`,
                value: `${user.firstname} ${user.lastname}`,
                url: `#mention_${user?.id}`,
            }));

        // Add "Everyone" mention if there are participants
        if (participantDetails.length > 0) {
            const everyoneId = participantDetails.map(user => user?.id).join(',');
            mentions.unshift({
                text: 'Everyone',
                value: 'Everyone',
                url: `#mention_${everyoneId}`,
            });
        }

        return mentions;
    };

    const trimEditorHtml = (html) => {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;

        // Remove empty <div><br></div> or <div></div> from start and end
        const children = Array.from(tempDiv.childNodes);

        // Helper to check if node is empty (like <div><br></div> or <div></div>)
        const isEmptyBlock = (node) => {
            return (
                node.nodeType === Node.ELEMENT_NODE &&
                node.nodeName === 'DIV' &&
                (node.innerHTML === '<br>' || node.innerHTML.trim() === '')
            );
        };

        // Trim from start
        while (children.length && isEmptyBlock(children[0])) {
            tempDiv.removeChild(children[0]);
            children.shift();
        }

        // Trim from end
        while (children.length && isEmptyBlock(children[children.length - 1])) {
            tempDiv.removeChild(children[children.length - 1]);
            children.pop();
        }

        return tempDiv.innerHTML.trim();
    };


    const resetEditor = () => {
        if (editorRef.current) {
            editorRef.current.innerHTML = '';
            setEditorHtml('');
            dispatch(setPopup({ key: 'raw_message_text', value: '' }));
        }
    };

    const updateEditorHtml = () => {
        setTimeout(() => {
            if (editorRef.current) {
                const rawHtml = editorRef.current.innerHTML;
                const cleaned = trimEditorHtml(rawHtml);
                setEditorHtml(cleaned);
                dispatch(setPopup({ key: 'raw_message_text', value: cleaned }))
            }
        }, 0);
    };

    // const getCaretCoordinates = () => {
    //     const sel = window.getSelection();
    //     if (!sel.rangeCount) return { top: 0, left: 0 };

    //     const range = sel.getRangeAt(0).cloneRange();
    //     range.collapse(true);
    //     const rect = range.getBoundingClientRect();
    //     return { top: rect.bottom + window.scrollY, left: rect.left + window.scrollX };
    // };

    const insertVoiceText = (text) => {
        const sel = window.getSelection();
        if (!sel.rangeCount) return;

        const range = sel.getRangeAt(0);
        range.deleteContents();

        const textNode = document.createTextNode(text);
        range.insertNode(textNode);

        // Move the cursor to the end of inserted text
        range.setStartAfter(textNode);
        range.collapse(true);
        sel.removeAllRanges();
        sel.addRange(range);

        updateEditorHtml(); // Update the editorHtml state
    };


    const {
        listening,
        transcript,
        resetTranscript,
        isMicrophoneAvailable,
        browserSupportsSpeechRecognition
    } = useSpeechRecognition();

    const ToggleVoice = (type) => {
        // Always focus the editor before toggling voice
        if (editorRef.current) {
            editorRef.current.focus();
            // Optionally, place caret at end if needed
            placeCaretAtEnd(editorRef.current);
        }
        if (browserSupportsSpeechRecognition) {
            if (isMicrophoneAvailable) {
                if (listening) {
                    SpeechRecognition.abortListening();
                    resetTranscript();
                } else {
                    SpeechRecognition.startListening({ continuous: true })
                }
            } else {
                toast.error("Mircrophone access denied !", { duration: 2000 });
            }
        } else {
            toast.error("Your Browser doesn't  support this feature", { duration: 2000 });
        }
        setVoice(type)
    }

    // Track last inserted transcript to avoid repeated insertion
    const lastTranscriptRef = useRef('');
    useEffect(() => {
        if (transcript.length > 0 && voice) {
            // Only insert the new part
            let newText = transcript;
            if (lastTranscriptRef.current && transcript.startsWith(lastTranscriptRef.current)) {
                newText = transcript.slice(lastTranscriptRef.current.length);
            }
            if (newText.length > 0) {
                const cc = (['quotation', 'semicolon', 'new line', 'dash', 'comma', 'underscore', 'full stop', 'dot', 'enter', 'exclamation mark', 'question mark', 'square bracket', 'round bracket', 'apostrophe', 'tab', 'space'].includes(newText) ? '' : ' ')
                    + newText
                        .replace(/tab/gi, '\t')
                        .replace(/new line|enter/gi, '\n')
                        .replace(/full stop|dot/gi, '.')
                        .replace(/space/gi, ' ')
                        .replace(/semicolon/gi, ';')
                        .replace(/underscore/gi, '_')
                        .replace(/hyphen/gi, '-')
                        .replace(/question mark/gi, '?')
                        .replace(/exclamation mark/gi, '!')
                        .replace(/quotation/gi, "'")
                        .replace(/comma/gi, ',')
                        .replace(/dash/gi, '-')
                        .replace(/square bracket/gi, '[]')
                        .replace(/round bracket/gi, '()')
                        .replace(/apostrophe|appostrophe/gi, "'");
                insertVoiceText(cc); // Insert only the new part
            }
            lastTranscriptRef.current = transcript;
        } else if (!voice) {
            lastTranscriptRef.current = '';
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [transcript, voice]);


    const handlePrivate = () => {
        if (active_conversation?.group === 'yes') {
            setMoreOptions(false)
            dispatch(setPopup({ key: 'private_msg', value: true }))
            dispatch(setPopup({ key: "upload_popup", value: true }));
        }
    }

    const handleInput = () => {
        const sel = window.getSelection();
        const text = sel.anchorNode?.textContent || '';
        const cursorIndex = sel.anchorOffset;
        const sliced = text.slice(0, cursorIndex);
        const match = sliced.match(/@(\w*)$/);

        if (match) {
            const filtered = MentionUsers()?.filter((user) =>
                user?.text?.toLowerCase().startsWith(match[1].toLowerCase())
            );

            setMentionUsers(filtered);
            setSearchText(match[1]);
            setShowPopup(true);
        } else {
            setShowPopup(false);
        }

        updateEditorHtml();
    };

    const insertMention = (user) => {
        const sel = window.getSelection();
        const range = sel.getRangeAt(0);
        range.setStart(range.startContainer, range.startOffset - searchText.length - 1);
        range.deleteContents();

        const link = document.createElement('a');
        link.href = user.url;
        link.textContent = `@${user.value}`;
        link.className = 'text-teal-500 dark:text-teal-400';
        link.setAttribute('data-mention', 'true');
        link.contentEditable = 'false';

        range.insertNode(link);
        range.collapse(false);
        sel.removeAllRanges();
        sel.addRange(range);

        setShowPopup(false);
        setMentionUsers([]);
        setSearchText('');

        updateEditorHtml();
    };

    const saveCaret = () => {
        const selection = window.getSelection();
        if (selection.rangeCount > 0) {
            savedRangeRef.current = selection.getRangeAt(0);
        }
    };
    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            if (!e.shiftKey) {
                e.preventDefault();
                handleSendMsg();
            }
        }
    };

    const handleEmojiSelect = (emoji) => {
        const editor = editorRef.current;
        editor.focus();

        if (savedRangeRef.current) {
            const range = savedRangeRef.current;
            const emojiNode = document.createTextNode(emoji.native);
            range.deleteContents();
            range.insertNode(emojiNode);
            range.setStartAfter(emojiNode);
            range.setEndAfter(emojiNode);
            const sel = window.getSelection();
            sel.removeAllRanges();
            sel.addRange(range);
        }

        // setShowEmojiPicker(false);
        updateEditorHtml();
    };

    function removeExtraBRTags(htmlString) {
        // Remove leading <br> tags
        var regexr = /<\/br>/;
        htmlString = htmlString.replace(regexr, '')
        // Remove trailing <br> tags
        htmlString = htmlString.replace(regexr, '')
        return htmlString.trimEnd();
    }

    function extractURLs(message) {
        if (!message) return []; // Return an empty array if no message

        // Helper function to decode HTML entities
        const decodeHTMLEntities = (str) => {
            const textarea = document.createElement("textarea");
            textarea.innerHTML = str;
            return textarea.value;
        };

        // Remove email addresses
        const cleanedMessage = message.replace(/(([a-zA-Z0-9._%+-]+@[a-zA-Z.-]+\.[a-zA-Z]{2,6}))/gi, '');

        // Replace HTML <br> and other tags with a space, then remove all remaining HTML tags
        const plainText = cleanedMessage
            .replace(/<br\s*\/?>/gi, ' ') // Replace <br> with a space
            .replace(/<[^>]+>/g, ''); // Remove other HTML tags

        // Find URLs using linkify
        const links = linkify.find(plainText, 'url');

        // Extract, decode, and return the array of URLs
        return links.map((link) => decodeHTMLEntities(link.href));
    }


    function mentionList(message) {
        console.log("🚀 ~ mentionList ~ message:", message)
        if (!message) return;

        var regex1 = /<(?:[^>]*?\s+)?href=(["'])\/profile(.*?)\1/ig

        var mentionId = message.match(regex1)
        console.log("🚀 ~ mentionList ~ mentionId:", mentionId)
        mentionId = mentionId?.toString();

        var regex2 = /<(?:[^>]*?\s+)?href=(["'])\/profile\/(.*?)/ig
        mentionId = mentionId?.replace(regex2, "");

        var regex3 = /"/ig
        mentionId = mentionId?.replace(regex3, "");
        var mentions = mentionId?.split(',');

        return mentions;
    }

    const isSendButtonActive = editorHtml?.trim().length > 0;

    const [send_msg_input] = useMutation(sendMsgMutation);
    const handleSendMsg = () => {
        if (upload_popup) {
            props.handleSend()
            return
        }
        if (!isSendButtonActive) { return; }

        // Utility to clean and format message content
        const formatMessage = (content) => {
            return removeExtraBRTags(content).replace(/[\r\n]/gm, '  </br>').replace(/<\/br>$/, '');
        };

        // Utility to extract mentions
        const extractMentions = (message) => {
            return message.includes('<a href="#mention_') ? mentionList(message) : [];
        };

        // Format the message content

        const formattedContent = formatMessage(editorHtml);
        const cleaned = trimEditorHtml(formattedContent)
        const urls = extractURLs(cleaned);
        const mentions = extractMentions(cleaned);

        // Create message payload
        const json_data = {
            conversation_id: type === 'task' ? props.task?.conversation_id : active_conversation?.conversation_id,
            company_id: user?.company_id,
            sender: user?.id,
            msg_body: aesEncrypt(cleaned),
            participants: type === 'task' ? props.task?.participants : reply_main_msg ? reply_main_msg?.participants : active_conversation?.participants,
            is_reply_msg: params.msg_id ? 'yes' : 'no',
            is_secret: reply_main_msg?.is_secret,
            reply_for_msgid: params.msg_id ? params.msg_id : '',
            task_id: type === 'task' ? props?.task?._id : undefined,
            msg_type: urls.length > 0 ? "text link" : type === 'task' ? "text discussion" : 'text',
            url_body: urls.length > 0 ? JSON.stringify(urls) : undefined,
            mention_user: mentions,
            flagged: props.flagged ? true : false,
        };

        if (voice) ToggleVoice(false);
        setEmojiView(false);

        resetEditor();
        // Send message and update state
        return new Promise(async (resolve, reject) => {
            try {
                const send_msg_data = await send_msg_input({ variables: { input: json_data } });
                placeCaretAtEnd(document.activeElement);

                const dycrepetMsg = await modifyMyMsg([send_msg_data.data.send_msg.msg]);
                dispatch(set_messages({
                    data: [...messages, ...dycrepetMsg],
                    is_reply_msg: params.msg_id ? true : false,
                    created_at: new Date().toISOString(),
                    my_new_msg: true
                }));

                // Clear local drafts
                const draftKey = params.msg_id || active_conversation?.conversation_id;
                localStorage.removeItem(`draft_${draftKey}`, '');

                // Scroll to bottom
                setTimeout(() => {
                    scrollToBottom('main_msg_scroll_div');
                }, 300);

                resolve(true);
            } catch (error) {
                toast.error(error.message);
                reject(error);
            }
        });

    };

    return (
        <>
            {
                chat_loader ? <CustomEditorSkeleton /> :
                    <div className={`bottom_bar group_conv ${position} dark:border-none dark:bg-[#050F23]${location.pathname?.includes('/filter') ? '!hidden' : ''}`}>
                        {type === "fileUpload" || type === 'reply' || type === 'task' ? (
                            <div onClick={() => ToggleVoice(!voice)} className="absolute bottom-[22px] left-[14px] w-[32px] h-[32px] flex items-center justify-center">
                                {voice && (
                                    <span className="absolute inset-0 rounded-full bg-blue-400 opacity-60 animate-ping"></span>
                                )}
                                <div className={`z-10 cursor-pointer ${voice ? 'microPhoneActiveIcons' : 'microPhoneIcons'}`} />
                            </div>
                        )
                            : props.flagged ? null :
                                <MdOutlineMoreVert onClick={() => setMoreOptions(!moreOptions)} className='text-2xl text-[#636E82] absolute bottom-[24px] left-[6px] dark:text-white' />
                        }
                        {
                            moreOptions &&
                            <div ref={ref} className='bg-white dark:bg-teal-900 dark:border dark:border-teal-200 shadow absolute pt-1 pb-1 bottom-[60px] h-[80px] rounded-md left-[5px] w-14 flex flex-col justify-around items-center'>
                                <div onClick={() => ToggleVoice(!voice)} className="relative w-[32px] h-[32px] flex items-center justify-center">
                                    {voice && (
                                        <span className="absolute inset-[2px] rounded-full bg-blue-400 opacity-60 animate-ping z-0"></span>
                                    )}
                                    <div className={`z-10 cursor-pointer ${voice ? 'microPhoneActiveIcons' : 'microPhoneIcons'}`} />
                                </div>
                                {(active_conversation?.group === 'yes' && !params.msg_id) &&
                                    <div onClick={handlePrivate} className="privateLock"> </div>
                                }
                            </div>
                        }
                        {(type !== 'reply' && type !== 'task' && type !== 'fileUpload' && !props.flagged) && user?.access?.includes("ai") &&
                            <div className="aiIconsGray absolute left-8 bottom-[24px]" onClick={() => props.setComingSoon(true)}></div>
                        }


                        <div>
                            <div className="emojiContainer flex justify-center">
                                {emojiView &&
                                    <div ref={emojiRef} className='h-[270px] overflow-hidden mb-3 border rounded-t-lg'>
                                        <Picker
                                            className="emojiPicker"
                                            set={'Apple'}
                                            data={emoji_data}
                                            previewPosition="none"
                                            skinTonePosition="none"
                                            maxFrequentRows={5}
                                            theme="light"
                                            onEmojiSelect={handleEmojiSelect}
                                        />
                                    </div>
                                }
                            </div>

                            <div className={props.flagged ? 'w-full' : ''}>
                                <div
                                    ref={editorRef}
                                    contentEditable
                                    className={`min-h-12 max-h-52 overflow-auto !pt-[14px] pl-3 pb-3 pr-32 border border-[#a0d2fd] text-sm rounded outline-none dark:text-white customEditor ${type === 'fileUpload' || type === 'task' || type === 'reply' ? 'ml-12' : props.flagged ? 'ml-0' : !user?.access?.includes("ai") ? "ml-9" : 'ml-16'}`}
                                    onInput={handleInput}
                                    onBlur={saveCaret}
                                    onClick={saveCaret}
                                    onKeyDown={handleKeyDown}
                                    data-placeholder={`Message ${type === 'task' ? 'discussion' : active_conversation?.title}`}
                                />
                                {showPopup && (
                                    <MentionPopup
                                        users={mentionUsers}
                                        onSelect={insertMention}
                                    />
                                )}
                            </div>

                            <div className="send_msg_opt">

                                {type !== 'fileUpload' && user?.access?.includes("file") && <div to="file-upload" className="attachment_icons" onClick={() => dispatch(setPopup({ key: 'upload_popup', value: true }))} />}

                                {emojiView ?
                                    <div className={`emojiClosee  ${type === "task" && "!mr-[16px] !ml-[6px]"}`} onClick={() => setEmojiView(false)} >
                                        <div id='closeEmoji' className="privateCloseIcon  text-2xl text-text-body -ml-[2px] dark:invert dark:brightness-0 dark:contrast-100" />
                                    </div> :
                                    <div className={`${type === "task" && "!mr-[16px] !ml-[6px]"}`} onClick={() => setEmojiView(true)}>
                                        <div className='emojiIconsGray !-ml-0'></div>
                                    </div>
                                }
                                {type === "task" ? null :
                                    <div className="attachment_selector dark:invert dark:brightness-0 dark:contrast-100" onClick={() => setVoice_record(true)} >
                                        <div className='audioIconsGray !-ml-0'></div>
                                    </div>
                                }
                                <div className="SendIconMsg dark:invert dark:brightness-0 dark:contrast-100" onClick={handleSendMsg}></div>
                            </div>

                        </div>
                    </div>
            }

            {voice_record && <VoiceMessageCon isOpen={voice_record} onClose={() => setVoice_record(false)} />}
        </>
    );
};

export default CustomEditor;